import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router';
import { Grid, CardContent, LinearProgress, withTheme, Button } from '@material-ui/core';
import moment from 'moment';
import FancyCard from './fancy/FancyCard';

import GuestPageSidebar from './profile/GuestPageSidebar';
import CartPageCalendar from './atom/CartPageCalendar';
import { useModals, MODAL_TYPE } from '../hooks/useModal';
import { useNotify } from '../hooks/useNotify';
import GuestPageBookingTable from './profile/GuestPageBookingTable';
import { useSwaggerClient } from '../hooks/useSwaggerClient';
import PaymentsTable from './profile/PaymentsTable';
import EmaillogTable from './profile/EmailLogsTable';
import FilterDropdown from './atom/FilterDropdown';
import AgentStatistics from './profile/agentstatistics';
import AgentActivityDashboard from './profile/agentactivitydashboard';
import { useGuestFields } from '../hooks/useGuestFields';
import ItineraryView from './profile/ItineraryView';
import { updateGuestDatesParams } from '../pages/activitycards/components/urlFiltering';
import UserAccountBalancePage from './profile/UserAccountBalancePage';

const GuestPage = ({ updateGuest, loggedInUser, customer, loginAsCustomerWithId }) => {
    const { guestId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const [guest_fields] = useGuestFields();
    const { showModal } = useModals();
    const { notifyError } = useNotify();
    const isAgent = loggedInUser?.role === 'agent';
    const isAdmin = loggedInUser?.role === 'admin';
    const agentId = loggedInUser?.id;
    const [pageView, setPageView] = useState(isAgent ? 'activity_dashboard' : 'table_view');
    const viewOptions = [
        { value: 'calendar_view', name: 'Calendar view' },
        { value: 'table_view', name: 'Table view' },
        { value: 'itinerary_view', name: 'Itinerary view' },
        { value: 'account_balance', name: 'Account balance' }
    ];
    if (isAgent && guestId === 'me') {
        // viewOptions.push({ value: 'statistics', name: 'Statistics' }); TODO: remove 'statistics' later, activity_dashboard is the long run
        viewOptions.unshift({ value: 'activity_dashboard', name: 'Dashboard' });
    }

    // Cancel flow: parse ?bookingItemId=X&flow=cancel from URL
    const cancelFlowBookingItemId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('flow') === 'cancel' && params.get('bookingItemId')) {
            return parseInt(params.get('bookingItemId'), 10) || null;
        }
        return null;
    }, [location.search]);

    const [autoOpenCancelId, setAutoOpenCancelId] = useState(cancelFlowBookingItemId);

    const handleAutoOpenHandled = useCallback(() => {
        setAutoOpenCancelId(null);
        // Clean URL params
        const params = new URLSearchParams(location.search);
        params.delete('bookingItemId');
        params.delete('flow');
        const cleanSearch = params.toString();
        history.replace({
            pathname: location.pathname,
            search: cleanSearch ? `?${cleanSearch}` : ''
        });
    }, [history, location.pathname, location.search]);

    const handleAutoOpenError = useCallback(
        bookingItem => {
            setAutoOpenCancelId(null);
            // Clean URL params
            const params = new URLSearchParams(location.search);
            params.delete('bookingItemId');
            params.delete('flow');
            const cleanSearch = params.toString();
            history.replace({
                pathname: location.pathname,
                search: cleanSearch ? `?${cleanSearch}` : ''
            });

            // Determine error reason
            let message = 'This booking cannot be cancelled.';
            if (
                bookingItem.status === 'cancelled' ||
                bookingItem.status === 'cancelled_full_refund'
            ) {
                message = 'This booking has already been cancelled.';
            } else if (bookingItem.status === 'denied') {
                message = 'This booking has been denied and cannot be cancelled.';
            } else if (
                !moment(bookingItem.activity_start).isAfter(moment())
            ) {
                message = 'This booking is for a past event and cannot be cancelled.';
            }

            showModal(MODAL_TYPE.FULLSCREEN_MESSAGE_MODAL, {
                title: 'Cannot Cancel Booking',
                content: message,
                buttonLabel: 'OK'
            });
        },
        [history, location.pathname, location.search, showModal]
    );

    const [
        guestData,
        guestLoading,
        guestLoadingError,
        _setGuestData,
        reloadGuestData
    ] = useSwaggerClient('users', 'getUserById', { userId: guestId }, [guestId]);

    const [bookingsData] = useSwaggerClient('users', 'getBookingsByUserId', {
        userId: guestId,
        future: true,
        itinerary: true
    });

    // Reset view when switching profiles
    useEffect(() => {
        if (!isAgent) {
            return;
        }

        setPageView(guestId === 'me' ? 'activity_dashboard' : 'table_view');
    }, [guestId, isAgent]);

    // wire guestLoadingError to notifyError
    useEffect(() => {
        // wire error to notifyerror
        if (guestLoadingError) {
            notifyError('Guest page cannot be loaded');
            history.push('/');
        }
    }, [guestLoadingError, notifyError, history]);

    const openGuestEditModal = useCallback(() => {
        showModal(MODAL_TYPE.CREATE_OR_UPDATE_GUEST, {
            customer: {
                ...guestData.guest
            },
            id: guestId,
            guest_fields,
            isAgent,
            isAdmin,
            handleSubmit: async editedGuest => {
                const payload = { ...editedGuest, id: guestId };
                // forcefully override id, so we can send in 'me' when editing own profile
                await updateGuest(payload);
                await reloadGuestData();
                updateGuestDatesParams(editedGuest);
                showModal(MODAL_TYPE.NONE);
            }
        });
    }, [
        showModal,
        guestData,
        guestId,
        guest_fields,
        isAgent,
        isAdmin,
        updateGuest,
        reloadGuestData
    ]);
    return (
        <Grid container spacing={4} className="page" direction="row">
            <Grid item xs={12} sm={12} md={4} lg={3} className="hide-in-print">
                <FancyCard>
                    {guestData?.guest && (
                        <GuestPageSidebar
                            guest={guestData?.guest}
                            openEditModal={openGuestEditModal}
                            isAgent={isAgent}
                            customer={customer}
                            updateGuest={updateGuest}
                            reloadGuestData={reloadGuestData}
                            guestId={guestId}
                        />
                    )}
                    {!guestData?.guest && guestLoading && (
                        <CardContent>
                            <div className="search-loading">
                                <div className="mb-2">Loading profile</div>
                                <LinearProgress className="search-bar" />
                            </div>
                        </CardContent>
                    )}
                </FancyCard>
                <FancyCard className="mt-2">
                    <PaymentsTable />
                </FancyCard>
                {(isAdmin || isAgent) && (
                    <FancyCard className="mt-2">
                        <EmaillogTable guestId={guestId} />
                    </FancyCard>
                )}
            </Grid>
            <Grid
                item
                container
                xs={12}
                sm={12}
                md={8}
                lg={9}
                spacing={2}
                alignContent="flex-start"
            >
                <Grid item container xs={12} justifyContent="flex-end" className="hide-in-print">
                    <Grid item>
                        <FilterDropdown
                            justify="flex-end"
                            label=""
                            value={pageView}
                            setValue={val => {
                                setPageView(val);
                            }}
                            options={viewOptions}
                        />
                    </Grid>
                    <Grid>
                        {pageView === 'itinerary_view' && (
                            <Button
                                style={{
                                    border: '1px solid #ddd',
                                    marginLeft: '10px',
                                    color: 'grey',
                                    borderRadius: '0px'
                                }}
                                size="medium"
                                target="_blank"
                                rel="noreferrer"
                                href={bookingsData?.itineraryPrintUrl}
                                disabled={bookingsData.bookingItems.length === 0}
                            >
                                Print
                            </Button>
                        )}
                    </Grid>
                    <Grid>
                        {pageView === 'calendar_view' && (
                            <Button
                                style={{
                                    border: '1px solid #ddd',
                                    marginLeft: '10px',
                                    color: 'grey',
                                    borderRadius: '0px'
                                }}
                                size="medium"
                                target="_blank"
                                rel="noreferrer"
                                onClick={window.print}
                            >
                                Print
                            </Button>
                        )}
                    </Grid>
                    {isAgent &&
                        guestId !== 'me' &&
                        (!customer || customer?.id !== guestData?.guest.id) && (
                            <Grid item style={{ paddingLeft: '8px' }}>
                                <Button
                                    style={{ whiteSpace: 'nowrap' }}
                                    variant="contained"
                                    color="default"
                                    onClick={() => {
                                        const customerId = guestData?.guest.id;
                                        loginAsCustomerWithId(
                                            customerId,
                                            showModal,
                                            () => notifyError('Login as customer failed'),
                                            updateGuestDatesParams
                                        );
                                    }}
                                >
                                    Login As
                                </Button>
                            </Grid>
                        )}
                </Grid>
                {pageView === 'calendar_view' && (
                    <CartPageCalendar
                        label="Bookings calendar"
                        guestId={guestId}
                        guest={guestData?.guest}
                    />
                )}
                {pageView === 'statistics' && <AgentStatistics agentId={agentId} />}
                {pageView === 'activity_dashboard' && <AgentActivityDashboard agentId={agentId} />}
                {pageView === 'itinerary_view' && (
                    <Grid>
                        <ItineraryView bookingsData={bookingsData} />
                    </Grid>
                )}
                {pageView === 'table_view' && (
                    <Grid item container xs={12}>
                        <GuestPageBookingTable
                            label="Upcoming bookings"
                            guestId={guestId}
                            isAgent={isAgent}
                            isAdmin={isAdmin}
                            future
                            autoOpenCancelForId={autoOpenCancelId}
                            onAutoOpenHandled={handleAutoOpenHandled}
                            onAutoOpenError={handleAutoOpenError}
                        />
                        <GuestPageBookingTable
                            label="Past bookings"
                            guestId={guestId}
                            isAdmin={isAdmin}
                            isAgent={isAgent}
                            autoOpenCancelForId={autoOpenCancelId}
                            onAutoOpenHandled={handleAutoOpenHandled}
                            onAutoOpenError={handleAutoOpenError}
                        />
                    </Grid>
                )}
                {pageView === 'account_balance' && (
                    <UserAccountBalancePage
                        guestId={guestId}
                        isAgent={isAgent}
                        isAdmin={isAdmin}
                        guest={guestData?.guest}
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default withTheme(GuestPage);
